import { useDeferredValue, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SUBSCRIPTION_STATUSES, type SubscriptionListItemResponse } from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { platformSubscriptionsApi } from '@/services/platform-subscriptions.api';
import { moduleCatalogApi } from '@/services/module-catalog.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';

/**
 * Every subscription on the platform — a `PLATFORM_SUPER_ADMIN` view, not
 * a tenant screen. `Subscriber` is the tenant; `Owner` is resolved from
 * its owning membership, the same way `/administration` resolves a
 * member's identity. Module chips reuse `moduleCatalogApi`'s already-set
 * commercial titles rather than showing a raw `moduleKey`.
 */
export function SubscriptionsPage() {
  const { t, locale } = useI18n();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [moduleKey, setModuleKey] = useState('');

  const deferredSearch = useDeferredValue(search);

  const modulesQuery = useQuery({
    queryKey: ['platform', 'modules'],
    queryFn: () => moduleCatalogApi.list(),
  });
  const titleByKey = new Map((modulesQuery.data ?? []).map((entry) => [entry.key, entry.title]));

  const query = useQuery({
    queryKey: ['platform', 'subscriptions', { search: deferredSearch, status, moduleKey }],
    queryFn: ({ signal }) =>
      platformSubscriptionsApi.list(
        {
          search: deferredSearch || undefined,
          status: status || undefined,
          moduleKey: moduleKey || undefined,
          limit: 50,
        },
        signal,
      ),
    placeholderData: (previous) => previous,
  });

  const subscriptions = query.data?.data ?? [];
  const isStale = deferredSearch !== search;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.subscriptions.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('platform.subscriptions.count', { count: subscriptions.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>
      </header>

      <div className="filters">
        <Field
          label={t('common.actions.search')}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('platform.subscriptions.searchPlaceholder')}
          name="search"
        />

        <div className="field">
          <label className="field-label" htmlFor="subscription-status">
            {t('common.labels.status')}
          </label>
          <select
            id="subscription-status"
            className="field-input"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{t('platform.subscriptions.allStatuses')}</option>
            {SUBSCRIPTION_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`platform.subscriptions.status.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="subscription-module">
            {t('platform.subscriptions.module')}
          </label>
          <select
            id="subscription-module"
            className="field-input"
            value={moduleKey}
            onChange={(event) => setModuleKey(event.target.value)}
          >
            <option value="">{t('platform.subscriptions.allModules')}</option>
            {(modulesQuery.data ?? []).map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && subscriptions.length === 0 && (
        <EmptyState
          title={
            search || status || moduleKey ? t('common.states.noResults') : t('common.states.empty')
          }
        />
      )}

      {subscriptions.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('platform.subscriptions.subscriber')}</th>
                <th scope="col">{t('platform.subscriptions.owner')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col">{t('platform.subscriptions.modules')}</th>
                <th scope="col">{t('platform.subscriptions.renewsOrEnds')}</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((row) => (
                <SubscriptionRow
                  key={row.tenantId}
                  row={row}
                  titleByKey={titleByKey}
                  locale={locale}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubscriptionRow({
  row,
  titleByKey,
  locale,
}: {
  row: SubscriptionListItemResponse;
  titleByKey: Map<string, string>;
  locale: string;
}) {
  const { t } = useI18n();

  const dateLabel = row.canceledAt
    ? t('platform.subscriptions.canceledOn', {
        values: { date: formatDate(row.canceledAt, locale) },
      })
    : (row.currentPeriodEnd ?? row.trialEndsAt)
      ? formatDate((row.currentPeriodEnd ?? row.trialEndsAt)!, locale)
      : '—';

  return (
    <tr>
      <td>
        <div className="subscriber-name">
          {row.tenantName}
          <span className="cell-mono person-card-meta">{row.tenantSlug}</span>
        </div>
      </td>
      <td dir="ltr" className="cell-mono">
        {row.ownerName ? (
          <>
            {row.ownerName}
            <br />
            {row.ownerEmail}
          </>
        ) : (
          (row.ownerEmail ?? <span className="cell-empty">—</span>)
        )}
      </td>
      <td>
        <StatusBadge
          status={row.subscriptionStatus ?? 'CANCELED'}
          label={
            row.subscriptionStatus
              ? t(`platform.subscriptions.status.${row.subscriptionStatus}`)
              : t('platform.subscriptions.noSubscription')
          }
        />
      </td>
      <td>
        {row.moduleKeys.length > 0 ? (
          <div className="badge-list">
            {row.moduleKeys.map((key) => (
              <span key={key} className="badge">
                {titleByKey.get(key) ?? key}
              </span>
            ))}
          </div>
        ) : (
          <span className="cell-empty">—</span>
        )}
      </td>
      <td>{dateLabel}</td>
    </tr>
  );
}
